import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

import { AlertCircleIcon, CheckIcon } from './Icons';
import styles from './ui.module.css';

interface Toast {
  readonly id: number;
  readonly text: string;
  readonly kind: 'info' | 'error';
}

interface ToastApi {
  notify(text: string): void;
  error(text: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((text: string, kind: Toast['kind']) => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((list) => [...list.slice(-2), { id, text, kind }]);
    window.setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id));
    }, kind === 'error' ? 6000 : 3200);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      notify: (text) => {
        push(text, 'info');
      },
      error: (text) => {
        push(text, 'error');
      },
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.toastHost} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={[styles.toast, toast.kind === 'error' ? styles.toastError : ''].join(' ')}>
            {toast.kind === 'info' ? <CheckIcon size={16} /> : <AlertCircleIcon size={16} />}
            {toast.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}
